import { useEffect, useRef, useState } from 'react'
import { Modal, Pressable, ScrollView, Text, useWindowDimensions, View } from 'react-native'
import { useLazyQuery, useMutation } from '@apollo/client'
import { Image } from 'expo-image'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { REDEEM_PROMO_TICKET } from '@/constants/graphql/mutations'
import { TRY_GET_PROMO_TICKET } from '@/constants/graphql/queries'
import type { BuyQueryResult, SellQueryResult } from '@/types/common'
import { formatMarketNumber, marketPresentation } from '@/config/marketPresentation'
import { useToast } from '@/components/base'
import { ChildOrderDetailSection } from '@/components/orders/child/DetailSection'
import { ChildOrderPaymentSection } from '@/components/orders/child/PaymentSection'
import { ImageViewerSection } from '@/components/orders/ImageViewerSection'
import { RevisingOrderSection } from '@/components/orders/RevisingOrderSection'

export function ChildOrderCompletedScreen({
  data,
  type,
  parentInternalKey,
  parentId
}: {
  data:
    | BuyQueryResult['userGetBuyOrderByInternalKey']
    | SellQueryResult['userGetSellOrderByInternalKey']['pairBuyOrder'][number]
  type: 'buy' | 'sell'
  parentInternalKey?: string
  parentId?: string
}) {
  const { t } = useTranslation()
  const { width: windowWidth } = useWindowDimensions()
  const { bottom } = useSafeAreaInsets()
  const toast = useToast()
  const defaultErrorMessage = t('orders:lotteryFailed')
  const defaultTicketLoadError = t('orders:lotteryTicketLoadFailed')

  const getGraphQlErrorMessage = (errors?: readonly { message?: string }[]) =>
    errors
      ?.map((error) => error.message?.trim())
      .filter((message): message is string => Boolean(message && message.length > 0))
      .join('\n')

  type PromoTicketPreview = {
    id: string
    promoId: string
    promoCode: string
    promoVersion: number
    issuedAt: string
    sourceEventId: string
    issueKey: string
  }

  type TryGetPromoTicketQuery = {
    tryGetPromoTicket: PromoTicketPreview | null
  }

  type RedeemPromoTicketResponse = {
    redeemPromoTicket: {
      ticketId: string
      promoId: string
      promoCode: string
      promoVersion: number
      rewardAmount?: string | null
      rewardLabel?: string | null
      rewardOutcome: string
      roll?: number | null
      alreadyRedeemed: boolean
    }
  }

  type RedeemPromoTicketVariables = {
    ticketId: string
  }

  const [promoModalVisible, setPromoModalVisible] = useState(false)
  const [pendingTicket, setPendingTicket] = useState<PromoTicketPreview | null>(null)
  const [redeemResult, setRedeemResult] = useState<RedeemPromoTicketResponse['redeemPromoTicket'] | null>(null)
  const [redeeming, setRedeeming] = useState(false)
  const redeemingRef = useRef(false)

  const hasRequestedRef = useRef(false)
  const retryAttemptedRef = useRef(false)
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastTicketIdRef = useRef<string | null>(null)

  const targetOrderId = data.id

  const [fetchPromoTicket, { data: promoTicketData }] = useLazyQuery<TryGetPromoTicketQuery>(TRY_GET_PROMO_TICKET, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    onError: (error) => {
      const message = getGraphQlErrorMessage(error.graphQLErrors) || error.message || defaultTicketLoadError
      toast.show({ variant: 'error', description: message })
    }
  })

  useEffect(() => {
    if (!targetOrderId || hasRequestedRef.current) {
      return
    }

    hasRequestedRef.current = true
    fetchPromoTicket({ variables: { orderId: targetOrderId } })

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
      }
    }
  }, [fetchPromoTicket, targetOrderId])

  useEffect(() => {
    if (!promoTicketData) {
      return
    }

    const ticket = promoTicketData.tryGetPromoTicket
    if (ticket) {
      if (ticket.id !== lastTicketIdRef.current) {
        lastTicketIdRef.current = ticket.id
        retryAttemptedRef.current = false
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current)
          retryTimeoutRef.current = null
        }
        setPendingTicket(ticket)
        setPromoModalVisible(true)
      }
    } else if (!retryAttemptedRef.current && targetOrderId) {
      retryAttemptedRef.current = true
      retryTimeoutRef.current = setTimeout(() => {
        fetchPromoTicket({ variables: { orderId: targetOrderId } })
      }, 1000)
    }
  }, [fetchPromoTicket, promoTicketData, targetOrderId])

  const [redeemPromoTicket] = useMutation<RedeemPromoTicketResponse, RedeemPromoTicketVariables>(REDEEM_PROMO_TICKET, {
    errorPolicy: 'all'
  })

  const handleRedeem = async () => {
    if (!pendingTicket || redeemingRef.current) {
      return
    }

    try {
      redeemingRef.current = true
      setRedeeming(true)
      const { data: redeemData, errors } = await redeemPromoTicket({
        variables: { ticketId: pendingTicket.id }
      })

      const graphQlMessage = getGraphQlErrorMessage(errors)
      if (graphQlMessage) {
        throw new Error(graphQlMessage)
      }

      const result = redeemData?.redeemPromoTicket
      if (!result) {
        throw new Error(defaultErrorMessage)
      }

      setRedeemResult(result)
    } catch (error) {
      const message = error instanceof Error && error.message.trim().length > 0 ? error.message : defaultErrorMessage
      toast.show({ variant: 'error', description: message })
    } finally {
      setRedeeming(false)
      redeemingRef.current = false
    }
  }

  const handleCloseModal = () => {
    setPromoModalVisible(false)
    setPendingTicket(null)
    setRedeemResult(null)
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }
  }

  return (
    <>
      <View className={'flex-1 bg-background-secondary'}>
        <ScrollView className={'flex-1'} contentContainerStyle={{ paddingBottom: bottom }}>
          <View className={'border-b border-border-10 bg-background-primary px-4 pb-[11px] pt-2'}>
            <Text className={'text-2xl font-semibold text-text-primary'}>{t('orders:transactionCompleted')}</Text>
          </View>
          <ChildOrderDetailSection data={data} type={type} parentInternalKey={parentInternalKey} />
          {data.dealAmount.value !== data.originDealAmount.value && (
            <RevisingOrderSection
              dealAmount={data.dealAmount}
              originDealAmount={data.originDealAmount}
              basePrice={data.basePrice}
              changeAmountProof={data.changeAmountProofBucketPath}
            />
          )}
          <ChildOrderPaymentSection data={data} type={type} />
          <ImageViewerSection images={data.paymentProofUrl} />
        </ScrollView>
      </View>

      <Modal
        visible={promoModalVisible && !!pendingTicket}
        transparent
        statusBarTranslucent
        onRequestClose={() => {
          if (redeemResult) {
            handleCloseModal()
          }
        }}
      >
        {!redeemResult ? (
          <>
            <View className={'absolute inset-0 z-20 bg-black opacity-70'} />
            <View className={'absolute inset-0 z-20 items-center justify-center'}>
              <Pressable
                className={'relative'}
                style={{ width: Math.min(windowWidth - 16, 480), aspectRatio: 480 / 560 }}
                disabled={redeeming}
                onPress={handleRedeem}
              >
                <Image
                  source={require('../../../../public/outside-of-envelope.png')}
                  accessibilityLabel={t('orders:redEnvelopeImage')}
                  style={{ position: 'absolute', inset: 0 }}
                  contentFit={'contain'}
                />
              </Pressable>
              <Pressable
                className={'mt-2 rounded-lg border border-[#A74C1D] bg-[#FFBC00] px-4 py-2'}
                disabled={redeeming}
                onPress={handleRedeem}
              >
                <Text className={'text-base font-bold text-[#A74C1D]'}>
                  {redeeming ? t('orders:drawing') : t('orders:draw')}
                </Text>
              </Pressable>
            </View>
          </>
        ) : (
          <RedeemResult redeemResult={redeemResult} handleClick={handleCloseModal} />
        )}
      </Modal>
    </>
  )
}

type RedeemResultData = {
  ticketId: string
  rewardOutcome: string
  rewardAmount?: string | null
  rewardLabel?: string | null
  alreadyRedeemed: boolean
}

export function RedeemResult({
  redeemResult,
  handleClick
}: {
  redeemResult: RedeemResultData | null
  handleClick: () => void
}) {
  const { t } = useTranslation()
  const { width } = useWindowDimensions()
  return (
    <>
      <View className={'absolute inset-0 z-20 bg-black opacity-70'} />
      <View className={'absolute inset-0 z-20 items-center justify-center'}>
        <Pressable
          className={'relative'}
          style={{ width: Math.min(width - 16, 480), aspectRatio: 480 / 320 }}
          onPress={handleClick}
        >
          <Image
            source={require('../../../../public/inside-of-envelope.png')}
            accessibilityLabel={t('orders:redEnvelopeImage')}
            style={{ position: 'absolute', inset: 0 }}
            contentFit={'contain'}
          />
          <RedeemResultText redeemResult={redeemResult} />
        </Pressable>
        <Pressable className={'mt-2 rounded-lg border border-[#A74C1D] bg-[#FFBC00] px-4 py-2'} onPress={handleClick}>
          <Text className={'text-base font-bold text-[#A74C1D]'}>{t('orders:finishDrawing')}</Text>
        </Pressable>
      </View>
    </>
  )
}

function RedeemResultText({ redeemResult }: { redeemResult: RedeemResultData | null }) {
  const { t } = useTranslation()
  if (redeemResult === null) {
    return null
  }

  if (redeemResult.alreadyRedeemed) {
    return (
      <View className={'absolute inset-0 items-center justify-center'}>
        <Text className={'text-base text-white'}>{t('orders:ticketUnavailable')}</Text>
      </View>
    )
  }

  if (redeemResult?.rewardOutcome !== 'NoPrize' && redeemResult?.rewardAmount && redeemResult?.rewardAmount !== '0') {
    return (
      <View className={'absolute inset-0 items-center justify-center gap-1'}>
        <View className={'flex-row items-center gap-1'}>
          <Text className={'text-base text-white'}>{t('orders:congratulations')}</Text>
          <Text className={'mx-1 text-[60px] leading-[72px] text-[#FFFF00]'}>
            {formatMarketNumber(redeemResult.rewardAmount, 'YBB')}
          </Text>
          <Text className={'text-base text-white'}>
            {t('orders:tokenRedEnvelope', { token: marketPresentation.platformToken.displayName })}
          </Text>
        </View>
        <Text className={'text-base text-white'}>{t('orders:redEnvelopeDelivered')}</Text>
      </View>
    )
  }

  return (
    <View className={'absolute inset-0 items-center justify-center'}>
      <Text className={'text-base text-white'}>{t('orders:noPrize')}</Text>
    </View>
  )
}
